import React from "react";
import "./smile-pages.css";

export default function IsSmilingContagious() {
  return (
    <div className="container-box mt-5 mb-5 smile-page">

      <h1>Is Smiling Contagious?</h1>

      <p>
        Yes, smiling <strong>is</strong> contagious! Smiling is contagious because of the way
        our brains are <strong>wired for social connection</strong> through a system of{" "}
        <strong>mirror neurons</strong>, emotional mimicry, and unconscious empathy. When we
        see someone smile, our brain automatically encourages us to{" "}
        <strong>mirror that smile</strong>, often without realizing it.
      </p>

      <p>Here’s a breakdown of the science behind this phenomenon:</p>

      <hr />

      {/* SECTION HEADER */}
      <h3>😊 Why Smiling Is Contagious</h3>

      {/* 1. Mirror Neurons */}
      <h4>1. Mirror Neurons: Your Brain Copies What It Sees</h4>

      <ul>
        <li>
          <strong>Mirror neurons</strong> are special brain cells that fire both when you
          perform an action and when you see someone else perform it.
        </li>
        <li>
          When you see someone smile, these neurons{" "}
          <strong>simulate the smile in your own brain</strong>, often triggering your
          facial muscles to mimic it.
        </li>
      </ul>

      <p>
        <strong>📚 Source:</strong>
        <br />
        Rizzolatti &amp; Craighero (2004) – Mirror neuron theory and its role
        in empathy and imitation.
      </p>

      <hr />

      {/* 2. Emotional Contagion */}
      <h4>2. Emotional Contagion: We Absorb the Feelings of Others</h4>

      <ul>
        <li>
          Emotional contagion is the unconscious tendency to{" "}
          <strong>“catch” emotions</strong> from people around us.
        </li>
        <li>
          Seeing someone smile <strong>makes us feel happier</strong>, which encourages us to
          smile back.
        </li>
      </ul>

      <p>
        <strong>📚 Study:</strong>
        <br />
        Hatfield, Cacioppo, &amp; Rapson (1994) – Emotional expressions can trigger similar
        emotions in observers through automatic mimicry.
      </p>

      <hr />

      {/* 3. Social Bonding */}
      <h4>3. Social Bonding: Smiling Builds Connection</h4>

      <ul>
        <li>
          Smiles are powerful social signals. When we see someone smile, we instinctively
          want to <strong>reciprocate as a way</strong> to build trust, harmony, and social
          cohesion.
        </li>
        <li>
          Returning a smile helps establish <strong>mutual goodwill</strong>, which is
          essential for healthy relationships.
        </li>
      </ul>

      <p>
        <strong>📚 Source:</strong>
        <br />
        Keltner, D. (2003) – <em>Expressions of emotion serve as social tools in human
        interaction.</em>
      </p>

      <hr />

      {/* 4. Evolutionary Psychology */}
      <h4>4. Evolutionary Psychology: Smiles Signaled Safety</h4>

      <ul>
        <li>
          In early human groups, a smile was a{" "}
          <strong>nonverbal sign of friendliness and safety.</strong>
        </li>
        <li>
          Our brains evolved to <strong>respond positively and automatically</strong> to
          smiles to promote cooperation and survival.
        </li>
      </ul>

      <hr />

      {/* BRAIN TABLE */}
      <h3>🧠 What Happens in the Brain</h3>

      <table className="table mt-3">
        <thead>
          <tr>
            <th>Brain Region</th>
            <th>Role in Smiling Contagion</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td><strong>Mirror neuron system</strong></td>
            <td>Imitates observed facial expressions</td>
          </tr>
          <tr>
            <td><strong>Amygdala</strong></td>
            <td>Processes emotional expressions</td>
          </tr>
          <tr>
            <td><strong>Prefrontal cortex</strong></td>
            <td>Interprets social meaning</td>
          </tr>
        </tbody>
      </table>

      <hr />

      {/* SUMMARY TABLE */}
      <h3>✅ Summary: Why Smiles Spread</h3>

      <table className="table mt-3">
        <thead>
          <tr>
            <th>Reason</th>
            <th>What It Does</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Mirror neurons</td>
            <td>Mimic the smile automatically</td>
          </tr>
          <tr>
            <td>Emotional contagion</td>
            <td>Causes you to “feel” the smile</td>
          </tr>
          <tr>
            <td>Social bonding instinct</td>
            <td>Encourages mutual smiling</td>
          </tr>
          <tr>
            <td>Evolutionary benefit</td>
            <td>Promotes safety and group cohesion</td>
          </tr>
        </tbody>
      </table>

      <hr />

      {/* KEYS TO REMEMBER */}
      <h3>🗝️ Keys to Remember</h3>

      <p>
        <strong>
          Smiling is contagious because your brain is designed to mirror and feel the
          emotions of others.
        </strong>{" "}
        When you see someone smile, your brain lights up, your mood lifts, and you’re
        likely to smile back—creating a ripple of positivity.
      </p>

      <p>
        <strong>Want to test it out?</strong> Use <strong>That Smile App</strong>, then try
        smiling at three strangers today and notice how many{" "}
        <strong>smile back!</strong> Then share That Smile App with a friend. 😄
      </p>

    </div>
  );
}


